import React, { useCallback, useEffect, useRef, useState } from "react";

const HOVER_CLOSE_DELAY_MS = 220;

/**
 * Magnifying-glass icon for the action-items search control.
 *
 * @example
 * <SearchLensIcon />
 * // Renders a 16×16 search lens SVG
 */
export const SearchLensIcon = () => (
  <svg
    className="action-items-search-toggle__icon"
    width="16"
    height="16"
    viewBox="0 0 13 13"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden="true"
  >
    <circle cx="5.5" cy="5.5" r="4" />
    <path d="M9 9l2.5 2.5" strokeLinecap="round" />
  </svg>
);

/**
 * Header icon button that toggles the action-items search field.
 *
 * @example
 * // Input — closed, no query
 * <ActionItemsSearchToggle searchOpen={false} hasQuery={false} onToggle={() => setOpen(true)} />
 * // Output — plain lens button, aria-expanded=false
 *
 * @example
 * // Input — open with text
 * <ActionItemsSearchToggle searchOpen hasQuery onToggle={() => setOpen(false)} />
 * // Output — active lens button, aria-expanded=true
 */
export const ActionItemsSearchToggle = ({
  searchOpen = false,
  hasQuery = false,
  onToggle,
  panelId = "action-items-search-panel",
}) => {
  const isActive = searchOpen || hasQuery;
  const label = searchOpen ? "Hide search" : "Search your tasks";

  return (
    <button
      type="button"
      className={[
        "action-items-search-toggle",
        "action-items-header-icon-btn",
        isActive ? "action-items-search-toggle--active" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={onToggle}
      aria-expanded={searchOpen}
      aria-controls={panelId}
      aria-label={label}
      title={label}
    >
      <SearchLensIcon />
    </button>
  );
};

/**
 * Expandable search field shown below the action-items header row.
 *
 * @example
 * // Input — value="follow up", user typing
 * <ActionItemsSearchPanel value="follow up" onChange={setQuery} panelId="action-items-search-panel" />
 * // Output — full-width search input with lens icon
 */
export const ActionItemsSearchPanel = ({
  value = "",
  onChange,
  onClose,
  inputRef,
  panelId = "action-items-search-panel",
  placeholder = "Search your tasks…",
  ariaLabel = "Search your tasks",
  className = "",
  onPointerDown,
}) => (
  <div
    id={panelId}
    className={["action-items-page-search", className].filter(Boolean).join(" ")}
    onPointerDown={onPointerDown}
  >
    <span className="action-items-page-search__icon" aria-hidden="true">
      <SearchLensIcon />
    </span>
    <input
      ref={inputRef}
      type="search"
      className="action-items-page-search__input"
      placeholder={placeholder}
      value={value}
      onChange={(event) => onChange?.(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          onClose?.();
        }
      }}
      aria-label={ariaLabel}
    />
  </div>
);

/**
 * Inline header search: preview on hover, edit on click, collapse on outside click.
 * When a query is set, the collapsed icon uses the same active dot + tint as filters.
 *
 * @example
 * // Input — idle
 * <ActionItemsExpandableSearch value="" onChange={setQuery} />
 * // Output — lens icon; hover expands field, click pins for typing
 *
 * @example
 * // Input — value="invoice", collapsed after blur
 * <ActionItemsExpandableSearch value="invoice" onChange={setQuery} />
 * // Output — active lens icon (dot + #8ea2ff tint)
 *
 * @example
 * searchRef.current.openAndFocus();
 * // Expands and focuses the search input
 */
export const ActionItemsExpandableSearch = React.forwardRef(function ActionItemsExpandableSearch(
  {
    value = "",
    onChange,
    inputRef: externalInputRef,
    panelId = "action-items-search-panel",
    placeholder = "Search your tasks…",
    ariaLabel = "Search your tasks",
    className = "",
    /** When false, expand only on click (not hover). Useful in narrow embeds like the landing demo. */
    expandOnHover = true,
  },
  ref
) {
  const wrapRef = useRef(null);
  const internalInputRef = useRef(null);
  const inputRef = externalInputRef || internalInputRef;
  const closeTimerRef = useRef(null);
  const isPinnedRef = useRef(false);
  const [isPinned, setIsPinned] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  isPinnedRef.current = isPinned;

  const hasQuery = Boolean(String(value).trim());
  const isExpanded = expandOnHover ? isPinned || isHovered : isPinned;

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const collapseSearch = useCallback(() => {
    clearCloseTimer();
    if (inputRef.current === document.activeElement) {
      inputRef.current.blur();
    }
    setIsPinned(false);
    setIsHovered(false);
  }, [clearCloseTimer, inputRef]);

  const pinSearch = useCallback(() => {
    clearCloseTimer();
    setIsHovered(true);
    setIsPinned(true);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, [clearCloseTimer, inputRef]);

  const toggleSearch = useCallback(() => {
    if (isPinnedRef.current || document.activeElement === inputRef.current) {
      collapseSearch();
      return;
    }
    pinSearch();
  }, [collapseSearch, pinSearch, inputRef]);

  React.useImperativeHandle(
    ref,
    () => ({
      openAndFocus: pinSearch,
      collapse: collapseSearch,
      toggle: toggleSearch,
      isOpen: () => Boolean(isPinnedRef.current),
    }),
    [pinSearch, collapseSearch, toggleSearch]
  );

  const openHover = useCallback(() => {
    clearCloseTimer();
    setIsHovered(true);
  }, [clearCloseTimer]);

  const scheduleCloseHover = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      if (!isPinnedRef.current) {
        setIsHovered(false);
      }
    }, HOVER_CLOSE_DELAY_MS);
  }, [clearCloseTimer]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  useEffect(() => {
    if (!isExpanded) return undefined;

    const handleOutside = (event) => {
      if (!wrapRef.current?.contains(event.target)) {
        collapseSearch();
      }
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [isExpanded, collapseSearch]);

  const rootClass = [
    "action-items-inline-search",
    isExpanded ? "action-items-inline-search--expanded" : "",
    isPinned ? "action-items-inline-search--pinned" : "",
    hasQuery ? "action-items-inline-search--has-query" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const handleShellPointerDown = (event) => {
    if (!isPinned) {
      event.preventDefault();
      pinSearch();
    }
  };

  return (
    <div
      ref={wrapRef}
      className={rootClass}
      onMouseEnter={expandOnHover ? openHover : undefined}
      onMouseLeave={expandOnHover ? scheduleCloseHover : undefined}
      onFocus={expandOnHover ? openHover : undefined}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          collapseSearch();
        }
      }}
    >
      <div
        className="action-items-inline-search__shell"
        onPointerDown={handleShellPointerDown}
        role="search"
      >
        <span className="action-items-page-search__icon" aria-hidden="true">
          <SearchLensIcon />
        </span>
        <input
          ref={inputRef}
          id={panelId}
          type="search"
          className="action-items-page-search__input action-items-inline-search__input"
          placeholder={placeholder}
          value={value}
          onChange={(event) => onChange?.(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              collapseSearch();
            }
          }}
          tabIndex={isExpanded ? 0 : -1}
          aria-label={ariaLabel}
          aria-hidden={!isExpanded}
        />
      </div>
    </div>
  );
});

export default ActionItemsSearchToggle;
